import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  CheckCircle, 
  XCircle, 
  Eye,
  Clock,
  MoreVertical,
  BookOpen,
  AlertCircle,
  Download,
  SlidersHorizontal,
  BookText,
  Edit,
  Trash2,
  User,
  PauseCircle,
  RefreshCw
} from 'lucide-react';
import { booksService, serviceService } from '../services/api';

const StatusBadge = ({ status }) => {
  const statusConfig = {
    approved: { color: 'bg-green-100 text-green-800 border border-green-200', icon: CheckCircle },
    pending: { color: 'bg-yellow-100 text-yellow-800 border border-yellow-200', icon: Clock },
    rejected: { color: 'bg-red-100 text-red-800 border border-red-200', icon: XCircle },
    draft: { color: 'bg-gray-100 text-gray-800 border border-gray-200', icon: Clock }
  };

  const config = statusConfig[status] || statusConfig.draft;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${config.color}`}>
      <Icon className="h-3 w-3 mr-1.5" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const BookDetailsModal = ({ book, isOpen, onClose, onStatusChange, onReprocess, queryClient }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);

  const { data: bookDetails } = useQuery({
    queryKey: ['book', book?.id],
    queryFn: () => booksService.getBook(book.id),
    enabled: !!book && isOpen
  });

  const loadPages = async () => {
    if (!book) return;
    
    setLoadingPages(true);
    try {
      const response = await booksService.getBookPages(book.id);
      setPages(response.pages || []);
    } catch (error) {
      console.error('Error loading pages:', error);
    } finally {
      setLoadingPages(false);
    }
  };

  // Reset pages when book changes
  React.useEffect(() => {
    if (book && isOpen) {
      setPages([]);
      setCurrentPage(0);
      setActiveTab('details');
    }
  }, [book?.id, isOpen]);

  React.useEffect(() => {
    if (isOpen && activeTab === 'read' && pages.length === 0) {
      loadPages();
    }
  }, [isOpen, activeTab]);

  if (!isOpen || !book) return null;

  const handleStatusChange = async (newStatus) => {
    setLoadingStatus(true);
    try {
      await onStatusChange(book.id, newStatus);
      onClose();
    } catch (error) {
      console.error('Error changing status:', error);
      alert(`Failed to update status: ${error.message}`);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleReprocess = async () => {
    try {
      await onReprocess(book.id);
      alert('Book reprocessing started');
    } catch (error) {
      alert('Error reprocessing book');
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/page-placeholder.png';
    
    // Priority 1: Already a full URL
    if (typeof imagePath === 'string' && imagePath.startsWith('http')) {
      // Apply the same cleaning logic as BookReader
      if (imagePath.startsWith('https://suavemente-media.lon1.cdn.digitaloceanspaces.com/book_pages/')) {
        return imagePath;
      }
      
      if (imagePath.includes('media/https%3A')) {
        const decoded = decodeURIComponent(imagePath);
        const match = decoded.match(/book_pages\/[^/]+\/page_\d+\.jpg/);
        if (match) {
          return `https://suavemente-media.lon1.cdn.digitaloceanspaces.com/${match[0]}`;
        }
      }
      
      if (imagePath.includes('media/')) {
        return imagePath.replace('media/media/', 'media/');
      }
      
      return imagePath;
    }
    
    // Priority 2: Check if it's a cover_image_url from admin upload
    if (typeof imagePath === 'object' && imagePath.cover_image_url) {
      return imagePath.cover_image_url;
    }
    
    // Priority 3: Construct DO Spaces URL from filename
    const bucketName = 'suavemente-media';
    const region = 'lon1';
    
    let path = imagePath;
    
    // Clean the path
    if (typeof path === 'string') {
      if (path.startsWith('/')) path = path.substring(1);
      if (path.startsWith('media/')) path = path.substring(6);
      
      // Check if it's a book page
      if (path.includes('book_pages/')) {
        // Already has book_pages in path
        if (!path.startsWith('book_pages/')) {
          path = `book_pages/${path}`;
        }
      } else if (path.includes('page_')) {
        // Has page_X pattern - add book_pages/ prefix
        if (book?.id && !path.includes('/')) {
          // If we have bookId and it's just a filename, organize by book
          path = `book_pages/${book.id}/${path}`;
        } else {
          path = `book_pages/${path}`;
        }
      }
      
      return `https://${bucketName}.${region}.cdn.digitaloceanspaces.com/media/${path}`;
    }
    
    return '/page-placeholder.png';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img
              src={book.cover_image_url || (book.cover_image?.url) || book.cover_image || '/book-placeholder.png'}
              alt={book.title}
              className="h-16 w-12 object-cover rounded-lg shadow-xs border border-gray-200"
            />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{book.title}</h2>
              <p className="text-gray-600 text-sm">by {book.author_name || book.author_details?.username || 'Unknown Author'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        <div className="border-b border-gray-200">
          <div className="px-6 flex space-x-8">
            {['details', 'read', 'actions'].map(tab => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  if (tab === 'read' && pages.length === 0) {
                    loadPages();
                  }
                }}
                className={`py-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab
                    ? 'border-[#1d1d1d] text-[#1d1d1d]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'details' ? 'Book Details' : 
                 tab === 'read' ? 'Read Book' : 'Actions'}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {activeTab === 'details' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Basic Information</h3>
                  <dl className="space-y-2">
                    <div><dt className="text-xs text-gray-500">Title</dt><dd className="text-sm font-medium">{book.title}</dd></div>
                    <div><dt className="text-xs text-gray-500">Author</dt><dd className="text-sm font-medium">{book.author_name || book.author_details?.username}</dd></div>
                    <div><dt className="text-xs text-gray-500">Genre</dt><dd className="text-sm font-medium">{book.genre_name}</dd></div>
                    <div><dt className="text-xs text-gray-500">Price</dt><dd className="text-sm font-medium">GHS {book.price}</dd></div>
                  </dl>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Status & Metadata</h3>
                  <dl className="space-y-2">
                    <div><dt className="text-xs text-gray-500">Status</dt><dd className="text-sm font-medium"><StatusBadge status={book.status} /></dd></div>
                    <div><dt className="text-xs text-gray-500">SVP ID</dt><dd className="text-sm font-medium font-mono">{book.svp_id}</dd></div>
                    <div><dt className="text-xs text-gray-500">Upload Date</dt><dd className="text-sm font-medium">{new Date(book.upload_date).toLocaleDateString()}</dd></div>
                    <div><dt className="text-xs text-gray-500">Total Pages</dt><dd className="text-sm font-medium">{book.total_pages}</dd></div>
                  </dl>
                </div>
              </div>

              {book.description && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{book.description}</p>
                </div>
              )}

              {book.author_bio && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Author Bio</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{book.author_bio}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'read' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-medium text-gray-500">Book Content</h3>
                <div className="flex items-center space-x-4">
                  {pages.length > 0 && (
                    <span className="text-xs text-gray-500">
                      Page {currentPage + 1} of {pages.length}
                    </span>
                  )}
                  <button
                    onClick={() => {
                      setPages([]); // Clear current pages
                      loadPages(); // Reload pages
                    }}
                    className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                    title="Refresh pages"
                    disabled={loadingPages}
                  >
                    <RefreshCw className={`h-4 w-4 ${loadingPages ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>

              {loadingPages ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1d1d1d]"></div>
                </div>
              ) : pages.length > 0 ? (
                <div className="bg-gray-50 rounded-lg p-6 min-h-[400px] flex flex-col items-center justify-center">
                  {pages[currentPage]?.image ? (
                    <img
                      src={getImageUrl(pages[currentPage].image)}
                      alt={`Page ${pages[currentPage].page_number}`}
                      className="max-w-full max-h-96 object-contain shadow-lg"
                    />
                  ) : pages[currentPage]?.content ? (
                    <div className="prose max-w-none">
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {pages[currentPage].content}
                      </p>
                    </div>
                  ) : (
                    <div className="text-center text-gray-500">
                      <BookText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No content available for this page</p>
                    </div>
                  )}

                  {pages.length > 1 && (
                    <div className="flex items-center space-x-4 mt-6">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                        disabled={currentPage === 0}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-gray-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(pages.length - 1, prev + 1))}
                        disabled={currentPage === pages.length - 1}
                        className="px-4 py-2 bg-[#1d1d1d] text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-gray-800"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <BookText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No pages available for this book</p>
                  <button
                    onClick={handleReprocess}
                    className="mt-4 px-4 py-2 bg-[#1d1d1d] text-white rounded-lg text-sm font-medium hover:bg-gray-800"
                  >
                    Reprocess PDF
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'actions' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-4">Change Book Status</h3>
                <div className="grid grid-cols-3 gap-4">
                  <button
                    onClick={() => handleStatusChange('approved')}
                    disabled={book.status === 'approved' || loadingStatus}
                    className="p-4 bg-green-50 border border-green-200 rounded-lg text-center disabled:opacity-50 hover:bg-green-100 transition-colors"
                  >
                    {loadingStatus ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto mb-2"></div>
                    ) : (
                      <>
                        <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-2" />
                        <div className="text-sm font-medium text-green-800">Approve</div>
                        <div className="text-xs text-green-600 mt-1">Make book public</div>
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={() => handleStatusChange('draft')}
                    disabled={book.status === 'draft' || loadingStatus}
                    className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center disabled:opacity-50 hover:bg-yellow-100 transition-colors"
                  >
                    {loadingStatus ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-600 mx-auto mb-2"></div>
                    ) : (
                      <>
                        <PauseCircle className="h-6 w-6 text-yellow-600 mx-auto mb-2" />
                        <div className="text-sm font-medium text-yellow-800">Put on Hold</div>
                        <div className="text-xs text-yellow-600 mt-1">Change to draft status</div>
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={() => handleStatusChange('rejected')}
                    disabled={book.status === 'rejected' || loadingStatus}
                    className="p-4 bg-red-50 border border-red-200 rounded-lg text-center disabled:opacity-50 hover:bg-red-100 transition-colors"
                  >
                    {loadingStatus ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600 mx-auto mb-2"></div>
                    ) : (
                      <>
                        <XCircle className="h-6 w-6 text-red-600 mx-auto mb-2" />
                        <div className="text-sm font-medium text-red-800">Reject</div>
                        <div className="text-xs text-red-600 mt-1">Remove from platform</div>
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-sm font-medium text-gray-500 mb-4">Other Actions</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Link
                    to={`/admin/books/${book.id}/edit`}
                    className="inline-flex items-center px-4 py-2.5 border border-gray-300 text-sm font-semibold rounded-xl text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md no-underline"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Link>
                  <button 
                    onClick={() => {
                      if (window.confirm('Are you sure you want to delete this book?')) {
                        booksService.deleteBook(book.id)
                          .then(() => {
                            alert('Book deleted successfully');
                            onClose();
                            window.location.reload();
                          })
                          .catch(() => alert('Error deleting book'));
                      }
                    }}
                    className="p-3 bg-red-50 border border-red-200 rounded-lg text-center hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="h-5 w-5 text-red-600 mx-auto mb-1" />
                    <div className="text-sm font-medium text-red-800">Delete Book</div>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function Books() {  
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    genre: ''
  });
  const [selectedBook, setSelectedBook] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const queryClient = useQueryClient();

  const { data: booksData, isLoading, error } = useQuery({
    queryKey: ['books', filters],
    queryFn: () => booksService.getAll(filters)
  });

  const { data: pendingBooks } = useQuery({
    queryKey: ['pending-books'],
    queryFn: () => booksService.getPending()
  });

  // Single mutation for all status changes using the existing endpoint
 const updateStatusMutation = useMutation({
  mutationFn: ({ bookId, status }) => 
    // Use adminUpdateStatus instead of updateBookStatus
    booksService.adminUpdateStatus(bookId, status),
  onSuccess: () => {
    // Refresh all relevant queries
    queryClient.invalidateQueries({ queryKey: ['books'] });
    queryClient.invalidateQueries({ queryKey: ['pending-books'] });
    queryClient.invalidateQueries({ queryKey: ['book'] });
    alert('Book status updated successfully!');
  },
  onError: (error) => {
    console.error('Status update failed:', error);
    alert(`Failed to update status: ${error.response?.data?.error || error.message || 'Unknown error'}`);
  }
});

  const reprocessMutation = useMutation({
    mutationFn: (bookId) => serviceService.reprocessBook(bookId)
  });

  const handleStatusChange = async (bookId, newStatus) => {
    // Confirm for rejections
    if (newStatus === 'rejected') {
      const reason = window.prompt('Please provide a reason for rejection:');
      if (reason === null) return; // User cancelled
      // Store reason if needed (you could send it in the API call)
    }
    
    // Optional: Add notes for other status changes
    if (newStatus === 'draft') {
      const notes = window.prompt('Optional: Enter notes for putting on hold:');
      // Continue even if user cancels
    }
    
    updateStatusMutation.mutate({ bookId, status: newStatus });
  };

  const handleReprocess = async (bookId) => {
    reprocessMutation.mutate(bookId);
  };

  const handleViewDetails = (book) => {
    setSelectedBook(book);
    setShowDetailsModal(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const getImageUrl = (imagePath) => {
  if (!imagePath) return '/book-placeholder.png';
  
  // Priority 1: Direct URL from cover_image_url (admin uploads)
  if (imagePath && imagePath.startsWith && imagePath.startsWith('http')) {
    return imagePath;
  }
  
  // Priority 2: Check if it's a cover_image_url from admin upload
  if (typeof imagePath === 'object' && imagePath.cover_image_url) {
    return imagePath.cover_image_url;
  }
  
  // Priority 3: Process filename/string for DO Spaces
  const bucketName = 'suavemente-media';
  const region = 'lon1';
  
  let path = imagePath;
  
  // Clean the path
  if (path.startsWith('/')) path = path.substring(1);
  if (path.startsWith('media/')) path = path.substring(6);
  if (path.startsWith('book_covers/')) path = path.substring(12);
  
  // Ensure proper path
  if (path && !path.startsWith('book_covers/')) {
    path = `book_covers/${path}`;
  }
  
  return `https://${bucketName}.${region}.cdn.digitaloceanspaces.com/media/${path}`;
};

  const books = booksData?.results || booksData || [];

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <AlertCircle className="mx-auto h-16 w-16 text-red-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error loading books</h3>
          <p className="text-gray-600 font-medium">Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Book Management</h1>
          <p className="text-gray-600 font-medium">Manage and review all books on the platform</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-500 bg-gray-100 px-4 py-2 rounded-xl font-semibold">
            {pendingBooks?.length || 0} books pending review
          </div>
          <Link
            to="/admin/upload-book"
            className="bg-gradient-to-r from-[#ff9702] to-[#ffaa33] text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:shadow-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center no-underline"
          >
            <BookOpen className="h-4 w-4 mr-2" />
            Upload Book
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-xs border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <SlidersHorizontal className="h-5 w-5 mr-2 text-[#1d1d1d]" />
            Filters
          </h3>
          <button
            onClick={() => setFilters({ search: '', status: '', genre: '' })}
            className="text-sm text-gray-500 hover:text-gray-700 font-medium"
          >
            Clear all
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Search Books</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by title or author..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10 w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-all duration-200"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-all duration-200"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="draft">Draft</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Genre</label>
            <select
              value={filters.genre}
              onChange={(e) => handleFilterChange('genre', e.target.value)}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-all duration-200"
            >
              <option value="">All Genres</option>
              <option value="fiction">Fiction</option>
              <option value="non-fiction">Non-Fiction</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ search: '', status: '', genre: '' })}
              className="w-full bg-gray-100 text-gray-700 px-4 py-3 rounded-xl hover:bg-gray-200 transition-all duration-200 font-semibold"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-xs border border-gray-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              All Books ({books.length})
            </h2>
            <div className="text-sm text-gray-500 font-medium">Sorted by: Latest</div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1d1d1d]"></div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {books.map((book) => (
              <div key={book.id} className="px-6 py-5 hover:bg-gray-50 transition-colors duration-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <img
                        src={book.cover_image_url || (book.cover_image?.url) || book.cover_image || '/book-placeholder.png'}                        alt={book.title}
                        className="h-20 w-14 object-cover rounded-lg shadow-xs border border-gray-200"
                      />
                      {book.status === 'pending' && (
                        <div className="absolute -top-1 -right-1">
                          <div className="bg-yellow-500 rounded-full p-1 shadow-xs border border-white">
                            <Clock className="h-2 w-2 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{book.title}</h3>
                      <p className="text-sm text-gray-600 font-medium mb-2">
                        by {book.author_name || book.author_details?.username || 'Unknown Author'}
                      </p>
                      <div className="flex items-center space-x-4">
                        <StatusBadge status={book.status} />
                        <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium">
                          {book.genre_name || 'No Genre'}
                        </span>
                        <span className="text-sm font-semibold text-gray-700">GHS {book.price || '0.00'}</span>
                        <span className="text-sm text-gray-500 font-medium">SVP ID: {book.svp_id || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    {book.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleStatusChange(book.id, 'approved')}
                          disabled={updateStatusMutation.isLoading}
                          className="inline-flex items-center px-4 py-2.5 border border-transparent text-sm font-semibold rounded-xl text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg"
                        >
                          {updateStatusMutation.isLoading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          ) : (
                            <CheckCircle className="h-4 w-4 mr-2" />
                          )}
                          Approve
                        </button>
                        <button
                          onClick={() => handleStatusChange(book.id, 'draft')}
                          disabled={updateStatusMutation.isLoading}
                          className="inline-flex items-center px-4 py-2.5 border border-gray-300 text-sm font-semibold rounded-xl text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
                        >
                          {updateStatusMutation.isLoading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400 mr-2"></div>
                          ) : (
                            <PauseCircle className="h-4 w-4 mr-2" />
                          )}
                          Put on Hold
                        </button>
                        <button
                          onClick={() => handleStatusChange(book.id, 'rejected')}
                          disabled={updateStatusMutation.isLoading}
                          className="inline-flex items-center px-4 py-2.5 border border-transparent text-sm font-semibold rounded-xl text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg"
                        >
                          {updateStatusMutation.isLoading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          ) : (
                            <XCircle className="h-4 w-4 mr-2" />
                          )}
                          Reject
                        </button>
                      </>
                    )}
                    
                    <button 
                      onClick={() => handleViewDetails(book)}
                      className="inline-flex items-center px-4 py-2.5 border border-gray-300 text-sm font-semibold rounded-xl text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {books.length === 0 && !isLoading && (
          <div className="text-center py-16">
            <BookOpen className="mx-auto h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No books found</h3>
            <p className="text-gray-600 font-medium mb-4">Try adjusting your search or filter criteria.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setFilters({ search: '', status: '', genre: '' })}
                className="bg-[#1d1d1d] text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-gray-800 transition-all duration-200"
              >
                Clear all filters
              </button>
              <Link
                to="/admin/upload-book"
                className="bg-gradient-to-r from-[#ff9702] to-[#ffaa33] text-white px-6 py-3 rounded-xl text-sm font-semibold hover:shadow-lg transition-all duration-200 no-underline text-center"
              >
                Upload First Book
              </Link>
            </div>
          </div>
        )}
      </div>

      <BookDetailsModal
  book={selectedBook}
  isOpen={showDetailsModal}
  onClose={() => {
    setShowDetailsModal(false);
    setSelectedBook(null);
  }}
  onStatusChange={handleStatusChange}
  onReprocess={handleReprocess}
  queryClient={queryClient}
/>
    </div>
  );
}